import {useMutation, useQueryClient} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {message} from "antd";
import {createProductServiceRepository} from "../data/repositories";
import {ProductService} from "../domain/product-service.entity";
import {UpdateProductServiceInput} from "../domain/product-service.types";
import {toCreateInput} from "./useCreateStoreServiceApi";
import {
  GET_STORE_SERVICE_RQ_KEY,
  GET_STORE_SERVICES_RQ_KEY,
} from "../../../react-query-hooks/store/services/constants";
import {
  UpdateStoreServiceApiPayload,
  UpdateStoreServiceApiResponse,
} from "../../../react-query-hooks/store/services/update-store-service/types";
import {CreateSessionApiBookingQuestionsPayload} from "../../../react-query-hooks/session/types";

/**
 * Adapts the legacy v1-shaped update payload into the canonical update input.
 */
const toUpdateInput = (
  payload: UpdateStoreServiceApiPayload
): UpdateProductServiceInput => ({
  ...toCreateInput(payload),
  serviceId: payload.serviceId,
});

/**
 * Maps the updated entity back to the legacy update response shape the edit
 * page and create wizard read (`data.updatedProductService.*`, snake_case).
 */
const toLegacyUpdateResponse = (
  entity: ProductService
): UpdateStoreServiceApiResponse => {
  const props = entity.toProps();

  return {
    updatedProductService: {
      id: props.id,
      userId: props.userId,
      name: props.name,
      slug: props.slug,
      tiny_url: props.tinyUrl,
      description: props.description,
      delivery_timeframe_unit: props.deliveryTimeframeUnit,
      delivery_timeframe_value: props.deliveryTimeframeValue,
      hideFromProfile: props.hideFromProfile,
      url_redirect: props.urlRedirect ?? "",
      statusId: props.statusId,
      request_phone_number: props.requestPhoneNumber,
      booking_questions:
        props.bookingQuestions.length > 0
          ? (props.bookingQuestions.map((q) => ({
              question: q.question,
              required: q.required,
              answer_type: q.answerType,
              ...(q.options ? {options: q.options} : {}),
            })) as CreateSessionApiBookingQuestionsPayload[])
          : null,
      createdAt: props.createdAt,
      updatedAt: props.updatedAt,
      deleted_at: null,
    },
  };
};

/**
 * Legacy-compatible update hook. Preserves the payload/response shape and the
 * single + list cache invalidations of the original, while routing through the
 * feature repository (always v2).
 *
 * @deprecated Use useUpdateProductService from features/product-services instead.
 */
export const useUpdateStoreServiceApi = () => {
  const queryClient = useQueryClient();
  const repository = createProductServiceRepository();

  return useMutation<
    {data: UpdateStoreServiceApiResponse},
    AxiosError<{message: string}>,
    UpdateStoreServiceApiPayload
  >({
    mutationFn: async (payload) => {
      const entity = await repository.update(toUpdateInput(payload));
      return {data: toLegacyUpdateResponse(entity)};
    },
    onError: (error: AxiosError<{message: string}>) => {
      if (error.message) {
        message.error(error.message);
      }
    },
    onSettled: (_data, _error, values) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [GET_STORE_SERVICE_RQ_KEY, String(values.serviceId)],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_STORE_SERVICES_RQ_KEY, {page: 1}],
        }),
      ]),
  });
};
